// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { ObservableOutput, AlertOutput, CaseOutput } from 'thehive-sdk';
import RouteBuilder from '@Services/RouteBuilder';
import { ResultCard } from '@Organisms/Search/SearchResultCard/ResultCard';
import { extractData } from './extractData';
import { useLocalMessage } from '@Helpers/locale';
import { formatObservableTitle } from '@Helpers/observable';
import { ICONS } from '@Constants/iconsApp';
import { PatternType } from '@Constants/patternsRoute';

import './ObservableResultCard.scss';

interface ObservableResultCardProps {
    data?: ObservableOutput;
}

interface ParentInformation {
    kind: 'alert' | 'case' | 'observable';
    data: AlertOutput | CaseOutput | undefined;
    link: [PatternType, Record<string, string>];
}
function getParentInformation(links: { [key: string]: any }, observableId: string): ParentInformation {
    if ('case' in links) {
        return {
            kind: 'case',
            data: links['case'],
            link: ['cases.observable', { caseId: links['case']._id, observableId }],
        };
    }
    if ('alert' in links) {
        return {
            kind: 'alert',
            data: links['alert'],
            link: ['alerts.observables', { alertId: links['alert']._id }],
        };
    }
    if ('report' in links && 'observable' in links) {
        const path = 'alert' in links.observable ? 'alerts.observable-report' : 'cases.observable-report';

        return {
            kind: 'observable',
            data: undefined,
            link: [
                path,
                {
                    caseId: links['observable'].case?._id,
                    alertId: links['observable'].alert?._id,
                    observableId: links['observable']._id,
                    reportId: links['report']._id,
                },
            ],
        };
    }
    return {
        kind: 'observable',
        data: undefined,
        link: ['cases.observable', {}],
    };
}

export const ObservableResultCard = ({ data }: ObservableResultCardProps): ReactElement => {
    const i = useLocalMessage();

    const {
        data: obsData = '',
        dataType = '',
        _createdAt = 0,
        _createdBy = '',
        tags = [],
        _id = '',
        attachment,
        message = '',
        extraData,
    } = data || {};

    const parentInformation = getParentInformation(extraData?.links || {}, _id);

    const title = `[${dataType}]: ${formatObservableTitle({ data: obsData, attachment })}`;

    return (
        <ResultCard
            data={{
                '#computeTitle': {
                    title,
                    path: RouteBuilder.compute.apply(RouteBuilder, parentInformation?.link || []),
                    icon: <ICONS.ObservableIcon />,
                },
                '#computeDate': {
                    date: _createdAt,
                    _createdBy,
                },
                '#computeDescription': message,
                '#computeTags': tags,
                '#computeLink':
                    parentInformation.kind !== 'observable'
                        ? { parentType: parentInformation.kind, parentData: parentInformation.data }
                        : undefined,
            }}
            extract={extractData({ locale: i })}
        />
    );
};
